import React from "react";

/**
 * Crop button hotfix (Vania 7 Oct 2026 · 11:20 UTC · live QA) ·
 * replaces every `window.confirm(...)` / `window.prompt(...)` /
 * `window.alert(...)` in the admin desk with an in-page overlay. The
 * Claude desktop app's embedded webview silently suppresses native
 * dialogs so staff presses do nothing — never again.
 *
 * Usage: controlled component. Parent owns `open` + `onConfirm` +
 * `onCancel`. Keyboard: Enter = confirm, Esc = cancel. Non-dismissable
 * by clicking the backdrop so staff always answer the question.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "yes, do it",
  cancelLabel = "cancel",
  tone = "default", // "default" | "danger"
  onConfirm,
  onCancel,
  busy = false,
  confirmDisabled = false,
  testIdPrefix = "confirm-dialog",
}) {
  const confirmBtnRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onCancel?.();
      if (e.key === "Enter" && !busy) onConfirm?.();
    };
    document.addEventListener("keydown", onKey);
    confirmBtnRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel, onConfirm]);
  if (!open) return null;
  return (
    <div
      data-testid={`${testIdPrefix}-overlay`}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
      role="dialog" aria-modal="true"
    >
      <div className="w-full max-w-md rounded-xl border border-neutral-900 bg-[#FDFBF7] p-5 shadow-xl">
        {title && (
          <h3 data-testid={`${testIdPrefix}-title`}
              className="mb-2 text-base font-semibold lowercase tracking-[0.02em]">
            {title}
          </h3>
        )}
        <div data-testid={`${testIdPrefix}-message`}
             className="text-sm text-neutral-700 whitespace-pre-wrap">
          {message}
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            data-testid={`${testIdPrefix}-cancel`}
            onClick={onCancel}
            disabled={busy}
            className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-semibold lowercase hover:bg-neutral-100 disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmBtnRef}
            data-testid={`${testIdPrefix}-confirm`}
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold lowercase disabled:opacity-60 ${
              tone === "danger"
                ? "bg-red-700 text-white hover:bg-red-800"
                : "bg-neutral-900 text-[#FDFBF7] hover:bg-neutral-800"
            }`}
          >
            {busy ? "working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Prompt-style dialog with a text input. Used for reject reasons etc. */
export function PromptDialog({
  open,
  title,
  message,
  placeholder = "",
  defaultValue = "",
  confirmLabel = "ok",
  cancelLabel = "cancel",
  onConfirm, // (value: string) => void
  onCancel,
  busy = false,
  testIdPrefix = "prompt-dialog",
}) {
  const [value, setValue] = React.useState(defaultValue);
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    setValue(defaultValue);
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onCancel?.();
    };
    document.addEventListener("keydown", onKey);
    setTimeout(() => inputRef.current?.focus(), 20);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, defaultValue, onCancel]);
  if (!open) return null;
  return (
    <div
      data-testid={`${testIdPrefix}-overlay`}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40"
      role="dialog" aria-modal="true"
    >
      <form
        onSubmit={(e) => { e.preventDefault(); if (!busy) onConfirm?.(value); }}
        className="w-full max-w-md rounded-xl border border-neutral-900 bg-[#FDFBF7] p-5 shadow-xl"
      >
        {title && (
          <h3 data-testid={`${testIdPrefix}-title`}
              className="mb-2 text-base font-semibold lowercase tracking-[0.02em]">
            {title}
          </h3>
        )}
        {message && (
          <div data-testid={`${testIdPrefix}-message`}
               className="mb-3 text-sm text-neutral-700 whitespace-pre-wrap">
            {message}
          </div>
        )}
        <input
          ref={inputRef}
          data-testid={`${testIdPrefix}-input`}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-md border border-neutral-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-900"
        />
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            data-testid={`${testIdPrefix}-cancel`}
            onClick={onCancel}
            disabled={busy}
            className="rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-semibold lowercase hover:bg-neutral-100 disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button
            type="submit"
            data-testid={`${testIdPrefix}-confirm`}
            disabled={busy}
            className="rounded-full bg-neutral-900 px-3 py-1.5 text-xs font-semibold lowercase text-[#FDFBF7] hover:bg-neutral-800 disabled:opacity-60"
          >
            {busy ? "working…" : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
